import React, { useState, useEffect } from 'react';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import Input, { Select, Checkbox } from '../../components/ui/Input';
import TimezoneSelect from '../../components/ui/TimezoneSelect';
import { adminApi } from '../../config/axios';
import { useGlobal } from '../../contexts/GlobalContext';
import { formatSize } from '../../config/utils';
import { useForm, Controller } from 'react-hook-form';

/**
 * The «Update Company» dialog, shared by the Companies list (pencil/«تعديل» on a card) and the company detail
 * page header («تعديل»). ONE form, ONE save path (PUT /companies/{id}/update), ONE data conversion: both entry
 * points pass the same `company` object (the whole Companies model: the datatable row and GET
 * /companies/{id}/show carry the same columns), so the dialog shows the same values from either place.
 *
 * Props: company (null = closed), onClose(), onSaved() (refresh whatever sits behind the dialog).
 */
export default function CompanyEditDialog({ company, onClose, onSaved }) {
  const { showToast } = useGlobal();
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm();

  useEffect(() => {
    if (!company) return;
    reset({
      name: company.name,
      status: company.status,
      account_type: company.account_type || 'standard',
      account_kind: company.account_kind === 'test' ? 'test' : 'customer',   // missing/unknown = customer, same as the backend
      timezone: company.timezone || null,   // null = genuinely unset; '' would look like a chosen value
      allowed_device_number: company.allowed_device_number,
      allowed_media_usage: parseInt(formatSize(company.allowed_media_usage)),
      nav_v6: company.nav_v6 === true || company.nav_v6 === 1,
    });
  }, [company, reset]);

  const onSave = async (data) => {
    setSaving(true);
    try {
      // '' means "not set" in the dropdown, but the backend rule is
      // nullable|in:<iana list> — an empty string is neither null nor a valid
      // zone, so it would 422. Send null instead.
      await adminApi.put(`/companies/${company.id}/update`, {
        ...data,
        timezone: data.timezone || null,
      });
      showToast('success', 'Updated', 'Company updated successfully.');
      onClose();
      onSaved?.();
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.message ?? 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!company}
      onClose={onClose}
      title="Update Company"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={saving} onClick={handleSubmit(onSave)}>
            {saving ? <Spinner size="sm" /> : 'Save Changes'}
          </button>
        </>
      }
    >
      <form className="space-y-4">
        {/* Per-company switch for the customer web's new navigation (prototype v6). At the TOP so it is the first
            thing seen. Off for everyone by default; the company's open platform follows on its next focus
            refetch. The web ignores it for restaurant accounts. Saves with Save Changes like every other field. */}
        <div className="rounded-lg border border-accent/40 bg-accent/10 p-3" dir="rtl">
          <Checkbox id="nav_v6" label="القائمة الجديدة (تجريبي)" {...register('nav_v6')} />
          <p className="mt-1 text-xs text-text-muted">يعرض القائمة الجديدة لهذه الشركة فقط. يمكن إيقافه في أي وقت.</p>
        </div>
        <Input label="Company Name" error={errors.name?.message}
          {...register('name', { required: 'Name is required' })} />
        <Select label="Status" {...register('status')}>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </Select>
        {/* Monitoring rule, system-enforced: every device of a «تجريبي» account is a test device (no ops alerts, absent
            from the daily summary), including devices added later. Admin-only; the backend logs each change. */}
        <div dir="rtl">
          <Select label="نوع الحساب" error={errors.account_kind?.message}
            {...register('account_kind', { required: 'نوع الحساب مطلوب' })}>
            <option value="customer">زبون</option>
            <option value="test">حساب تجريبي</option>
          </Select>
          <p className="mt-1 text-xs text-text-muted">أجهزة الحساب التجريبي لا تُصدر تنبيهات ولا تظهر في الملخص اليومي.</p>
        </div>
        <Select label="Account Type" {...register('account_type')}>
          <option value="standard">Standard (full dashboard)</option>
          <option value="restaurant">Restaurant (video-wall only)</option>
        </Select>
        {/* The venue's wall-clock timezone. Until now this could ONLY be set by
            the owner in their own Profile page, so a company onboarded without
            one silently inherited the Asia/Riyadh fallback with no admin-side
            way to correct it. Blank = leave unchanged (the request rule is
            nullable and update() saves validated() only). */}
        <Controller
          name="timezone"
          control={control}
          render={({ field }) => (
            <TimezoneSelect label="Timezone" value={field.value || null} onChange={field.onChange} allowUnset />
          )}
        />
        <p className="-mt-2 text-xs text-text-muted">
          A branch with its own timezone overrides this for the screens in that branch.
        </p>
        <Input label="Max Devices" type="number" error={errors.allowed_device_number?.message}
          {...register('allowed_device_number', { required: 'Required' })} />
        <Input label="Max Storage (MB)" type="number" error={errors.allowed_media_usage?.message}
          {...register('allowed_media_usage', { required: 'Required' })} />
      </form>
    </Modal>
  );
}
